import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import languageCodes from "./languageCodes";

const LANGUAGES = languageCodes.map((language) => language.code);
const FALLBACK_LNG = "en";

// Locale JSON is shared with the legacy frontend and served from
// public/locales; do not move or fork it.
//
// public/locales files are static and cached by the browser/CDN by URL.
// __LOCALE_VERSION__ is stamped at build time (see vite.config.mts) so the
// cache-bust token changes on every build, with no manual step. The guard
// keeps a working default if the bundle is built without the define.
/* global __LOCALE_VERSION__ */
const LOCALE_VERSION =
  typeof __LOCALE_VERSION__ !== "undefined" ? __LOCALE_VERSION__ : "dev";
const loadPath = (lng, ns) => `/locales/${lng}/${ns}.json?v=${LOCALE_VERSION}`;

// Set at init time: each portal entrypoint loads only its own namespaces, so
// one portal's bundle never fetches another portal's locale files.
let allowedNamespaces = [];

const lazyLoadBackend = {
  type: "backend",
  init() {},
  read(language, namespace, callback) {
    const load = async (lng, ns) => {
      if (!LANGUAGES.includes(lng) || !allowedNamespaces.includes(ns)) {
        throw new Error(`Unknown language or namespace: ${lng}/${ns}`);
      }
      const response = await fetch(loadPath(lng, ns));
      if (!response.ok) throw new Error(`Failed to load ${lng}/${ns}`);
      return response.json();
    };

    load(language, namespace)
      .then((resources) => {
        callback(null, resources);
      })
      .catch((err) => {
        if (language !== FALLBACK_LNG) {
          load(FALLBACK_LNG, namespace)
            .then((resources) => callback(null, resources))
            .catch((fallbackErr) => callback(fallbackErr, null));
        } else {
          callback(err, null);
        }
      });
  },
};

// Called once by each translated entrypoint with its namespace list.
export function initI18n(namespaces) {
  allowedNamespaces = namespaces;
  i18n
    .use(lazyLoadBackend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      supportedLngs: LANGUAGES,
      fallbackLng: FALLBACK_LNG,
      ns: namespaces,
      defaultNS: "common",
      interpolation: {
        escapeValue: false, // react already safes from xss
      },
      detection: {
        lookupQuerystring: "locale",
      },
      // Don't preload: the backend loads on demand
      partialBundledLanguages: true,
    });
  return i18n;
}

export default i18n;
